import clsx from "clsx";
import type { ReactNode } from "react";

export function TimesheetEditorTableShell({
  children,
  empty,
  emptyText = "Noch keine Einträge.",
}: {
  children: ReactNode;
  empty?: boolean;
  emptyText?: string;
}) {
  if (empty) {
    return (
      <div className="rounded-xl border border-dashed border-separator bg-fill-tertiary/30 px-4 py-6 text-center">
        <p className="text-sm text-label-secondary">{emptyText}</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-separator bg-card shadow-sm">
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

export function TimesheetEditorTable({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <table className={clsx("min-w-full border-collapse text-sm", className)}>{children}</table>;
}

export function TimesheetEditorTableHead({ children }: { children: ReactNode }) {
  return (
    <thead>
      <tr className="border-b border-separator bg-fill-secondary/70 text-left text-[11px] font-semibold uppercase tracking-wide text-label-secondary">
        {children}
      </tr>
    </thead>
  );
}

export function TimesheetEditorTableHeadCell({
  children,
  className,
  align = "start",
}: {
  children?: ReactNode;
  className?: string;
  align?: "start" | "end";
}) {
  return (
    <th className={clsx("px-3 py-2", align === "end" && "text-end", className)}>
      {align === "end" ? <div className="flex justify-end">{children}</div> : children}
    </th>
  );
}

export function TimesheetEditorTableFoot({ children }: { children: ReactNode }) {
  return <tfoot className="border-t-2 border-separator bg-fill-secondary/45">{children}</tfoot>;
}

export function TimesheetEditorTableBody({ children }: { children: ReactNode }) {
  return <tbody className="divide-y divide-separator/60">{children}</tbody>;
}

export function TimesheetEditorTableRow({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <tr
      className={clsx(
        "transition-colors hover:bg-fill-tertiary/40",
        className,
      )}
    >
      {children}
    </tr>
  );
}

export function TimesheetEditorTableCell({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <td className={clsx("px-3 py-2.5 align-middle text-label", className)}>{children}</td>;
}

export function TimesheetEditorRowActions({
  onEdit,
  onRemove,
  editLabel = "Bearbeiten",
  editDisabled = false,
}: {
  onEdit: () => void;
  onRemove: () => void;
  editLabel?: string;
  editDisabled?: boolean;
}) {
  return (
    <div className="flex justify-end gap-1 timesheet-editor-row-actions">
      <button
        type="button"
        disabled={editDisabled}
        title={editLabel}
        aria-label={editLabel}
        onClick={onEdit}
        className={clsx(
          "inline-flex h-7 w-7 items-center justify-center rounded-md border border-separator bg-card text-label-secondary transition",
          editDisabled
            ? "cursor-not-allowed opacity-40"
            : "hover:border-accent/30 hover:bg-accent-soft/40 hover:text-accent",
        )}
      >
        <svg viewBox="0 0 20 20" fill="none" aria-hidden className="h-4 w-4">
          <path
            d="M13.5 3.5l3 3L7 16H4v-3L13.5 3.5z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <button
        type="button"
        title="Entfernen"
        aria-label="Entfernen"
        onClick={onRemove}
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-separator bg-card text-label-secondary transition hover:border-status-red/30 hover:bg-status-red/10 hover:text-status-red"
      >
        <svg viewBox="0 0 20 20" fill="none" aria-hidden className="h-4 w-4">
          <path
            d="M6 6l8 8M14 6l-8 8"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
}

export function TimesheetEditorAngebotBadge({
  label,
  className,
}: {
  label: string;
  className?: string;
}) {
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full border border-separator/60 bg-fill-tertiary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-label-secondary",
        className,
      )}
    >
      {label}
    </span>
  );
}

export function TimesheetEditorHeroBadge({ className }: { className?: string }) {
  return (
    <span
      className={clsx(
        "inline-flex shrink-0 align-middle items-center rounded bg-accent-soft/60 px-1 py-px text-[9px] font-semibold uppercase leading-none tracking-wide text-accent",
        className,
      )}
    >
      Hero
    </span>
  );
}
